// Components==============
import storeWrongAnswer from 'actions/wrongAnswer/store';
import Column from 'components/UI/ExerciseColumn/Column';
import HiddenInput from 'components/UI/HiddenInput/HiddenInput';
import StatusBox, { BoxTypes } from 'components/UI/StatusBox/StatusBox';
import { motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { HomeworkContext } from 'screens/Homework/Homework';
import { compareArray } from 'utilities/compareArray';
import { convertArray } from 'utilities/convertArray';
import { SoundGroupExerciseContext } from '../../SoundGroupExercise';
import { useBlockSize } from '../../useBlockSize';
import Group from '../Group/Group';
import Text from '../Text/Text';
import './Category.module.scss';
// =========================

const columnVariants = {
    visible: {
        opacity: 1,
        visibility: 'visible' as 'visible',
    },
    hidden: {
        opacity: 0,
        visibility: 'hidden' as 'hidden',
    },
};

export default function Category({
    word,
    index,
    inHistory,
}: {
    word: WordsWithImages;
    index: number;
    inHistory?: boolean;
}) {
    const [text, setText] = useState('');
    const [displayInput, setDisplayInput] = useState(false);
    const [boxState, setBoxState] = useState<BoxTypes>('initial');
    const [attempts, setAttempts] = useState(0);

    const { sequence, background } = useContext(HomeworkContext);

    const {
        useImages,
        useWords,
        answer,
        setUserIsTypingAnswer,
        useInput,
        setUserAnswers,
        currentAnswer,
        setUserBarAnswer,
        givenAnswers,
    } = useContext(SoundGroupExerciseContext);

    const dispatch = useDispatch();
    const { treatmentId, homeworkId } = useParams() as {
        treatmentId: string;
        homeworkId: string;
    };

    const name = word?.name;
    const pivot = word?.pivot?.string;
    const image = word?.images[0]?.path;
    const klankgroepen = word?.klankgroepen;

    const alreadyAnswered = givenAnswers?.find((a) => a.id === word?.id);

    const mutatedArr = currentAnswer.map((a, i) =>
        convertArray(a).slice(5 - klankgroepen?.[i]?.length, 5)
    );

    useEffect(() => {
        setAttempts(0);
    }, [word]);

    const getIsCorrect = (answered?: boolean) => {
        const userAnswer = answered
            ? alreadyAnswered?.bars
            : currentAnswer?.map((a, i) =>
                  convertArray(a).slice(5 - klankgroepen?.[i]?.length, 5)
              );

        if (userAnswer?.length !== word.klankgroepen.length) return;

        const arrayIsEqual = userAnswer
            ?.map((array, i) => compareArray(array, word?.klankgroepen[i]))
            .every((c) => c === true);

        if (answered) return arrayIsEqual;

        const passesYellowBlockCheck =
            typeof userAnswer
                ?.map((array, i) => {
                    if (
                        answer?.klankgroepen[i][0] === 'ge' &&
                        currentAnswer[i][4 - array.length] === 'long-sound'
                    )
                        return true;
                    return false;
                })
                .find((exists) => exists) !== 'boolean';

        return arrayIsEqual && passesYellowBlockCheck;
    };

    // set values if answer is already given
    useEffect(() => {
        if (alreadyAnswered?.correctBars) {
            setText(alreadyAnswered.correctText);

            const isCorrect = getIsCorrect(true);
            if (isCorrect) return setBoxState('correct');
            setBoxState('failed');
            return;
        }
        if (useInput) setDisplayInput(false);
        if (!useInput) setText('');
        setText('');
        setBoxState('initial');
    }, [alreadyAnswered, name]); // name is added here to fix bug when user has slow network connection and alreadyAnswered would not exists yet when the user goes to next word. In that case the this useeffect would not fire. That caused the exercise to get stuck because this component would not reset to its initial state

    function storeWrong() {
        dispatch(
            storeWrongAnswer({
                given_answer: JSON.stringify(mutatedArr),
                correct_answer: JSON.stringify({
                    klankgroepen: answer?.klankgroepen,
                    woord: answer?.name,
                }),
                metadata: {
                    sequence,
                },
                type: 'klankgroepen',
                exercise_id: homeworkId,
                exercise_type: 'homework',
                treatment_id: treatmentId,
            })
        );
    }

    function tryAgain() {
        storeWrong();
        setBoxState('wrong');
        setTimeout(() => {
            setBoxState('submit');
        }, 1500);
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (
            boxState === 'initial' ||
            boxState === 'failed' ||
            boxState === 'wrong'
        )
            return;

        if (alreadyAnswered) return;

        const isCorrect = getIsCorrect();

        if (isCorrect || attempts === 2) {
            if (attempts === 2 && !isCorrect) {
                storeWrong();
                setBoxState('failed');
                setAttempts(0);
            } else setBoxState('correct');

            if (useInput) {
                setDisplayInput(true);
                setUserIsTypingAnswer(true);
                setUserBarAnswer(mutatedArr);
            } else {
                setText(answer?.name);
                setAttempts(0);
                setUserAnswers((prev) => [
                    ...prev,
                    {
                        id: answer.id,
                        bars: mutatedArr,
                        text: answer.name,
                        correctText: answer.name,
                        correctBars: answer.klankgroepen,
                    },
                ]);
            }
        } else {
            setAttempts((prev) => prev + 1);
            if (attempts < 3) tryAgain();
        }
    }

    const size = useBlockSize(klankgroepen?.length);

    return (
        <motion.div
            variants={columnVariants}
            animate="visible"
            initial="hidden"
            exit="hidden"
            styleName="wrapper"
        >
            {!inHistory && (
                <div styleName="columnWrapper">
                    <Column
                        useImages={useImages}
                        useWords={useWords}
                        name={pivot || name}
                        image={image}
                        background={background}
                        disablePadding
                    >
                        {}
                    </Column>
                </div>
            )}
            <div
                styleName={`itemWrapper ${
                    parseFloat(size) > 30 ? 'large-icon' : ''
                }`}
            >
                <div styleName="groupWrapper">
                    {klankgroepen.map((groupAnswer, i) => (
                        <Group
                            key={i}
                            boxState={boxState}
                            setBoxState={setBoxState}
                            index={i}
                            id={`group-${i}-column-${index}`}
                            name={word?.name}
                            blockSize={size}
                            alreadyAnswered={alreadyAnswered}
                        />
                    ))}
                </div>
                <form
                    onSubmit={handleSubmit}
                    styleName="statusBox"
                    style={{
                        height: `calc(${size} * 1.03)`,
                        width: `calc(${size} * 1.03)`,
                    }}
                >
                    <StatusBox boxState={boxState} fullSize />
                    <HiddenInput effect={currentAnswer} />
                </form>
                {text && (
                    <div styleName="text">
                        <Text text={text} />
                    </div>
                )}
                {displayInput && (
                    <div styleName="text">
                        <Text active={word === answer} />
                    </div>
                )}
            </div>
        </motion.div>
    );
}
